import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { FeatureLink } from "@softmax/web-features/navigation";

import { isNavigationClick } from "../link-click";

import joinedStyles from "../account-pill.module.css";

export function HeaderDropdown({
  label,
  href,
  id,
  open,
  hydrated,
  onOpenChange,
  onNavigate,
  align = "right",
  children,
}: {
  readonly label: string;
  readonly href?: string | undefined;
  readonly id: string;
  readonly open: boolean;
  readonly hydrated: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onNavigate: (href: string) => void;
  readonly align?: "left" | "right";
  readonly children: ReactNode;
}) {
  const slot = useRef<HTMLLIElement>(null);
  const hoveredByMouse = useRef(false);
  const [focusRequest, setFocusRequest] = useState(0);
  const focusedRequest = useRef(0);
  useLayoutEffect(() => {
    if (!open || focusRequest === focusedRequest.current) return;

    focusedRequest.current = focusRequest;
    const element = slot.current;

    if (element === null)
      throw new Error(
        "The open dropdown must be mounted before focusing its links.",
      );

    element.querySelector<HTMLAnchorElement>(":scope > ul a")?.focus();
  }, [open, focusRequest]);

  function onTriggerKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key !== "ArrowDown") return;

    event.preventDefault();
    setFocusRequest((request) => request + 1);
    onOpenChange(true);
  }

  return (
    <li
      ref={slot}
      className={joinedStyles.slot}
      onPointerEnter={(event) => {
        hoveredByMouse.current = event.pointerType === "mouse";

        if (hydrated && hoveredByMouse.current) onOpenChange(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse") hoveredByMouse.current = false;

        if (
          event.pointerType === "mouse" &&
          !event.currentTarget.contains(document.activeElement)
        )
          onOpenChange(false);
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          onOpenChange(false);
      }}
    >
      <div className={joinedStyles.menuTrigger}>
        {href && (
          <FeatureLink
            href={href}
            className={`${joinedStyles.link} ${joinedStyles.menuLabel}`}
            onKeyDown={onTriggerKeyDown}
            onClick={(event) => {
              if (isNavigationClick(event)) onNavigate(href);
            }}
          >
            {label}
          </FeatureLink>
        )}
        <button
          type="button"
          data-cogent-safe
          className={`${joinedStyles.link} ${href ? joinedStyles.menuArrow : joinedStyles.menuButton}`}
          aria-label={href ? `${label} menu` : undefined}
          aria-expanded={open}
          aria-controls={id}
          disabled={!hydrated}
          onKeyDown={onTriggerKeyDown}
          onClick={(event) =>
            onOpenChange(
              event.detail > 0 && hoveredByMouse.current ? true : !open,
            )
          }
        >
          {!href && label}
          <svg width="9" height="5" viewBox="0 0 9 5" aria-hidden="true">
            <path
              d="m1 .5 3.5 3.5L8 .5"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      {open && (
        <ul
          id={id}
          className={`${joinedStyles.menu} ${joinedStyles.dropdown} ${align === "left" ? joinedStyles.dropdownLeft : ""}`}
          aria-label={`${label} menu`}
        >
          {children}
        </ul>
      )}
    </li>
  );
}
